
import React from "react";
import { useNavigate } from "react-router-dom";
import useModuleProgress from "../hooks/useModuleProgress";
import "../styles/pages/_module-pages.scss";
import "../styles/pages/_learn-assessments.scss";
import "../styles/pages/_quiz-completion.scss";

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: "What is Workplace Stress?",
    content: [
      "<strong>Learning Goal:</strong> Understand workplace stress and its impact on health, mood, and performance.",
      "<br/>",
      "<strong>Key Topics:</strong>",
      "• What is workplace stress?",
      "• Effects of stress on health and work",
      "• Common stressors",
      "<br/>",
      "<strong>Discussion:</strong> Workplace stress is a physical and emotional response to job demands. It can affect your health, mood, and performance."
    ]
  },
  {
    title: "How to Manage Stress",
    content: [
      "Practical ways to manage stress:",
      "<br/>",
      "• Identify stressors and talk about them with someone you trust",
      "• Practice relaxation techniques like deep breathing or meditation",
      "• Stay organized and plan your day",
      "• Seek support from colleagues or professionals if needed",
      "<br/>",
      "<strong>Mini Practice:</strong> Try a 2-minute breathing exercise when you feel stressed."
    ]
  },
  {
    title: "Reflection",
    content: [
      "Think about your main sources of stress at work:",
      "• How do you currently cope with them?",
      "• What new strategies can you try?",
      "<br/>",
      "<strong>Journal Prompt:</strong> 'Today, I managed my stress by…'"
    ]
  },
  {
    title: "Ready to Test Your Knowledge?",
    content: [
      "You've completed Module 2: Managing Workplace Stress! Now, let's reinforce your learning with a quick quiz."
    ],
    additionalSection: {
      title: "Next Steps",
      content: [
        "Click 'Take the Quiz' below to test your knowledge about managing workplace stress.",
        "The quiz will help you review key concepts about stress and coping strategies."
      ]
    }
  }
];

const OccupationalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress("occupationalM2");
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
  navigate("/panel/occupational-module-2/quiz");
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate("/panel/occupational-resources");
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left occupational-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Managing Workplace Stress</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Understand workplace stress and its impact on health, mood, and performance.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? "Exit Module" : "Previous"}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate("/panel/occupational-module-2/quiz")}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule2Page;


